import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  NO_OPERATIONAL_PERIOD_LABEL,
  useCurrentPeriod,
  useIsEscalated,
  useOperationalPeriodLabels,
} from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useAutoZoomToHomeAor } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/useAutoZoomToHomeAor';
import { useIsSitStatMode } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useIsSitStatMode';
import {
  allowNextExclusiveMapOpen,
  getMapExclusivePageLabel,
  getMapOpenDestination,
  isMapExclusiveLocation,
} from 'components/MapPage/utils/mapExclusiveSurfaces';
import classNames from 'classnames';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useHubChromeEnabled,
  useNotificationCenterFlag,
} from 'utils/featureFlags';
import { formatCompactOpTimestamp } from 'utils/operationalPeriodUtils';

/** Planning-P dashboard: `/iap/:id` index or `/iap/:id/dashboard`. */
const isPlanningPPathname = (pathname: string) =>
  /\/iap\/[^/]+\/dashboard\/?$/.test(pathname) ||
  /\/iap\/[^/]+\/?$/.test(pathname);

const OP_CHIP_BADGE_CLASS =
  'tw:h-auto tw:gap-1 tw:whitespace-normal tw:border-white/25 tw:bg-transparent tw:px-2 tw:py-1 tw:font-normal tw:text-white';

const OP_CHIP_BADGE_CLASS_CFA =
  'tw:h-auto tw:gap-1 tw:whitespace-normal tw:border-border tw:bg-transparent tw:px-2 tw:py-1 tw:font-normal tw:text-foreground';

function useShowOpPeriodChips(): boolean {
  const location = useLocation();
  const isEscalated = useIsEscalated();
  const { showPeriodToggle } = useOperationalPeriodLabels();
  return (
    showPeriodToggle && (!isEscalated || isPlanningPPathname(location.pathname))
  );
}

/** Read-only OP window chips for the dark app header (beside incident name). */
export function OpPeriodWindowChips({ className }: { className?: string }) {
  const hubChrome = useHubChromeEnabled();
  const showChips = useShowOpPeriodChips();
  const { currentPeriod, periodNumber, startDate, anticipatedEnd } =
    useCurrentPeriod();
  const { currentPeriodLabel, futurePeriodNumber } =
    useOperationalPeriodLabels();
  const { data: incidentInfo } = useIncidentInfo(showChips);

  if (!showChips) {
    return null;
  }

  const currentStartIso =
    currentPeriod?.start_date_time ??
    (startDate?.isValid() ? startDate.toISOString() : null);
  const currentEndIso =
    currentPeriod?.anticipated_end_date_time ??
    currentPeriod?.end_date_time ??
    (anticipatedEnd?.isValid() ? anticipatedEnd.toISOString() : null);

  const nextStartIso = incidentInfo?.nextOperationalPeriodStartDateTime ?? null;
  const nextEndIso = incidentInfo?.nextOperationalPeriodEndDateTime ?? null;

  const currentOpLabel =
    periodNumber > 0
      ? `OP ${currentPeriodLabel}`
      : `OP ${NO_OPERATIONAL_PERIOD_LABEL}`;
  const nextOpLabel = `OP ${futurePeriodNumber}`;

  const chipClass = hubChrome ? OP_CHIP_BADGE_CLASS_CFA : OP_CHIP_BADGE_CLASS;

  return (
    <div
      className={cn(
        'tw:ml-2 tw:flex tw:max-w-[min(36rem,55vw)] tw:flex-shrink tw:flex-wrap tw:items-center tw:gap-1.5 tw:text-[10px] tw:leading-tight',
        className
      )}
      aria-label="Operational period windows"
    >
      <Badge variant="outline" className={chipClass}>
        <span
          className={
            hubChrome ? 'tw:text-muted-foreground' : 'tw:text-white/90'
          }
        >
          {currentOpLabel}
        </span>
        <span
          className={
            hubChrome
              ? 'tw:font-medium tw:text-foreground'
              : 'tw:font-medium tw:text-white'
          }
        >
          {formatCompactOpTimestamp(currentStartIso)}
          {' → '}
          {formatCompactOpTimestamp(currentEndIso)}
        </span>
      </Badge>
      <Badge variant="outline" className={chipClass}>
        <span
          className={
            hubChrome ? 'tw:text-muted-foreground' : 'tw:text-white/90'
          }
        >
          {nextOpLabel}
        </span>
        <span
          className={
            hubChrome
              ? 'tw:font-medium tw:text-foreground'
              : 'tw:font-medium tw:text-white'
          }
        >
          {formatCompactOpTimestamp(nextStartIso)}
          {' → '}
          {formatCompactOpTimestamp(nextEndIso)}
        </span>
      </Badge>
    </div>
  );
}

/** Right-cluster control: Show Map / Exit Map (same header slot). */
export function ToggleMapButton() {
  const hubChrome = useHubChromeEnabled();
  const hideNotifications = useNotificationCenterFlag();
  const { isMapVisible, setIsMapVisible } = useUscgMapContext();
  const { zoomToHomeAor } = useAutoZoomToHomeAor();
  const isSitStatMode = useIsSitStatMode();
  const location = useLocation();
  const navigate = useNavigate();
  const isEscalated = useIsEscalated();
  const [leaveOpen, setLeaveOpen] = useState(false);

  const exclusivePageLabel = getMapExclusivePageLabel(
    location.pathname,
    location.search,
    { isEscalated }
  );
  const isExclusive = isMapExclusiveLocation(
    location.pathname,
    location.search
  );

  const openMap = async () => {
    setIsMapVisible(true);
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
    try {
      await zoomToHomeAor();
    } catch (error) {
      console.error('Failed to zoom to home AOR:', error);
    }
  };

  const confirmLeaveAndOpenMap = async () => {
    const destination = getMapOpenDestination(location.pathname, {
      hideNotifications,
    });
    const destPath = destination?.split('?')[0] ?? location.pathname;
    const destSearch = destination?.includes('?')
      ? `?${destination.split('?')[1]}`
      : '';
    if (isMapExclusiveLocation(destPath, destSearch) || !destination) {
      allowNextExclusiveMapOpen();
    }
    if (destination) {
      navigate(destination);
    }
    setLeaveOpen(false);
    await openMap();
  };

  const handleToggle = async () => {
    if (isMapVisible) {
      setIsMapVisible(false);
      return;
    }
    if (isExclusive) {
      setLeaveOpen(true);
      return;
    }
    await openMap();
  };

  if (isSitStatMode) {
    return null;
  }

  const leaveDialog = (
    <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
      <AlertDialogContent className="dark tw:text-foreground">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Do you want to leave {exclusivePageLabel ?? 'this page'} and open
            the Map?
          </AlertDialogTitle>
          <AlertDialogDescription>
            The map cannot stay open on this page. Opening it will take you away
            from {exclusivePageLabel ?? 'this page'}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => void confirmLeaveAndOpenMap()}>
            Open Map
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (hubChrome) {
    return (
      <>
        <Button
          type="button"
          size="sm"
          variant={isMapVisible ? 'default' : 'outline'}
          className="tw:ms-1 tw:me-1 tw:gap-1"
          onClick={() => void handleToggle()}
          aria-label={isMapVisible ? 'Exit Map' : 'Show Map'}
          title={isMapVisible ? 'Exit Map' : 'Show Map'}
        >
          {isMapVisible ? (
            <>
              <span>Exit Map</span>
              <SharedIcon iconName="close" />
            </>
          ) : (
            <>
              <span>Show Map</span>
              <SharedIcon iconName="map" />
            </>
          )}
        </Button>
        {leaveDialog}
      </>
    );
  }

  return (
    <>
      <StylishNewButton
        className={classNames('ms-1 me-1 gap-1', {
          'button--primary': isMapVisible,
          'button--secondary': !isMapVisible,
        })}
        onClick={handleToggle}
        aria-label={isMapVisible ? 'Exit Map' : 'Show Map'}
        title={isMapVisible ? 'Exit Map' : 'Show Map'}
      >
        {isMapVisible ? (
          <>
            <span>Exit Map</span>
            <SharedIcon iconName="close" />
          </>
        ) : (
          <>
            <span>Show Map</span>
            <SharedIcon iconName="map" />
          </>
        )}
      </StylishNewButton>
      {leaveDialog}
    </>
  );
}
